import { useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, RefreshCw, Store } from 'lucide-react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import { Badge } from '@lecartte/shared-ui/components/Badge';
import { TextInput } from '@lecartte/shared-ui/components/TextInput';
import type { RestaurantsPageQuery } from '@/__generated__/RestaurantsPageQuery.graphql';
import type { RestaurantsPageVisibilityMutation } from '@/__generated__/RestaurantsPageVisibilityMutation.graphql';
import {
  countByVisibility,
  filterRestaurants,
  formatVisibilityChange,
  type VisibilityFilter,
} from '@/lib/restaurantVisibility';

const QUERY = graphql`
  query RestaurantsPageQuery {
    adminRestaurants {
      id
      name
      slug
      ownerEmail
      isApproved
      isPublic
      lastVisibilityChange {
        adminName
        isPublic
        reason
        createdAt
      }
    }
  }
`;

const VISIBILITY_MUTATION = graphql`
  mutation RestaurantsPageVisibilityMutation($input: AdminSetRestaurantVisibilityInput!) {
    AdminSetRestaurantVisibility(input: $input) {
      success
      error
    }
  }
`;

const FILTERS: { value: VisibilityFilter; label: string }[] = [
  { value: 'ALL', label: 'Todos' },
  { value: 'PUBLIC', label: 'Públicos' },
  { value: 'PRIVATE', label: 'Privados' },
];

function VisibilitySwitch({
  isPublic,
  disabled,
  name,
  onToggle,
}: {
  isPublic: boolean;
  disabled: boolean;
  name: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isPublic}
      aria-label={`${name}: ${isPublic ? 'público' : 'privado'}`}
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        isPublic ? 'bg-green-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block size-5 rounded-full bg-white shadow transition-transform ${
          isPublic ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function RestaurantsContent({
  fetchKey,
  onChanged,
}: {
  fetchKey: number;
  onChanged: () => void;
}) {
  const [search, setSearch] = useState('');
  const [visibility, setVisibility] = useState<VisibilityFilter>('ALL');
  const [processing, setProcessing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [commitVisibility] =
    useMutation<RestaurantsPageVisibilityMutation>(VISIBILITY_MUTATION);

  const data = useLazyLoadQuery<RestaurantsPageQuery>(
    QUERY,
    {},
    { fetchKey, fetchPolicy: 'network-only' }
  );

  const restaurants = data.adminRestaurants;
  const counts = countByVisibility({ restaurants });
  const visible = filterRestaurants({ restaurants, search, visibility });

  const changeVisibility = ({ id, isPublic }: { id: string; isPublic: boolean }) => {
    setProcessing(id);
    setError(null);
    commitVisibility({
      variables: { input: { restaurantId: id, isPublic, reason: reason.trim() || null } },
      onCompleted: (res) => {
        setProcessing(null);
        if (!res.AdminSetRestaurantVisibility.success) {
          setError(res.AdminSetRestaurantVisibility.error ?? 'Não foi possível alterar a visibilidade.');
          return;
        }
        setConfirming(null);
        setReason('');
        onChanged();
      },
      onError: () => {
        setProcessing(null);
        setError('Não foi possível alterar a visibilidade.');
      },
    });
  };

  const handleToggle = ({ id, isPublic }: { id: string; isPublic: boolean }) => {
    if (isPublic) {
      setConfirming(id);
      setReason('');
      return;
    }
    changeVisibility({ id, isPublic: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="md:w-80">
          <TextInput
            label="Buscar restaurante"
            aria-label="Buscar restaurante"
            placeholder="Nome, slug ou email do dono"
            value={search}
            onChange={({ value }) => setSearch(value)}
          />
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por visibilidade">
          {FILTERS.map((filter) => (
            <Button
              key={filter.value}
              variant={visibility === filter.value ? 'primary' : 'outline'}
              size="sm"
              aria-pressed={visibility === filter.value}
              onClick={() => setVisibility(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </div>

      <p className="text-sm text-slate-500">
        <span className="font-semibold text-slate-900">{counts.total}</span>{' '}
        {counts.total === 1 ? 'restaurante' : 'restaurantes'} ·{' '}
        <span className="font-semibold text-green-700">{counts.publicCount}</span>{' '}
        {counts.publicCount === 1 ? 'público' : 'públicos'} ·{' '}
        <span className="font-semibold text-slate-700">{counts.privateCount}</span>{' '}
        {counts.privateCount === 1 ? 'privado' : 'privados'}
      </p>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {visible.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
            <Store size={24} className="text-slate-400" />
          </div>
          <p className="font-semibold text-slate-700">Nenhum restaurante encontrado</p>
          <p className="text-sm text-slate-400 mt-1">Ajuste a busca ou o filtro.</p>
        </div>
      )}

      {visible.map((restaurant) => {
        const lastChange = formatVisibilityChange({ change: restaurant.lastVisibilityChange });
        return (
          <motion.div
            key={restaurant.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-slate-800 truncate">{restaurant.name}</h3>
                  <Badge
                    label={restaurant.isPublic ? 'Público' : 'Privado'}
                    variant={restaurant.isPublic ? 'success' : 'neutral'}
                  />
                  {!restaurant.isApproved && <Badge label="Conta sem aprovação" variant="warning" />}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 truncate">
                  @{restaurant.slug}
                  {restaurant.ownerEmail && ` · ${restaurant.ownerEmail}`}
                </p>
                {lastChange && <p className="text-xs text-slate-500 mt-1">{lastChange}</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
                  {restaurant.isPublic ? <Eye size={14} /> : <EyeOff size={14} />}
                  {restaurant.isPublic ? 'Visível' : 'Oculto'}
                </span>
                <VisibilitySwitch
                  isPublic={restaurant.isPublic}
                  name={restaurant.name}
                  disabled={processing === restaurant.id}
                  onToggle={() => handleToggle({ id: restaurant.id, isPublic: restaurant.isPublic })}
                />
              </div>
            </div>

            {confirming === restaurant.id && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-col gap-3">
                <p className="text-sm text-amber-900">
                  Tornar <strong>{restaurant.name}</strong> privado remove o restaurante da listagem, da busca e do
                  sitemap, e o endereço passa a mostrar “Restaurante não encontrado”. Novos pedidos ficam bloqueados.
                  O dono continua acessando o painel e os pedidos existentes continuam com rastreio.
                </p>
                <TextInput
                  label="Motivo (opcional)"
                  aria-label="Motivo (opcional)"
                  maxLength={300}
                  value={reason}
                  onChange={({ value }) => setReason(value)}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                    Cancelar
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    loading={processing === restaurant.id}
                    onClick={() => changeVisibility({ id: restaurant.id, isPublic: false })}
                  >
                    Tornar privado
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

export function RestaurantsPage() {
  const [fetchKey, setFetchKey] = useState(0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900">Restaurantes</h1>
          <p className="text-slate-500 text-sm mt-1">
            Controle quais restaurantes aparecem para o público. Só a equipe da Lecartte pode tornar um restaurante
            privado.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setFetchKey((k) => k + 1)}>
          <RefreshCw size={14} />
          Atualizar
        </Button>
      </div>
      <Suspense
        fallback={
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
            Carregando...
          </div>
        }
      >
        <RestaurantsContent fetchKey={fetchKey} onChanged={() => setFetchKey((k) => k + 1)} />
      </Suspense>
    </div>
  );
}
